import Check from '@mui/icons-material/Check';
import ChevronRight from '@mui/icons-material/ChevronRight';
import FilterList from '@mui/icons-material/FilterList';
import { buttonClasses, paperClasses } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { AccountType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { debounce, omit, snakeCase, uniqBy } from 'lodash-es';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import AppliedFiltersChips from '~components/Filters/AppliedFiltersChips';
import FilterMenuItem, { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import Menu from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { InfiniteQuery } from '~queries/constants';
import useAccountsInfiniteQuery from '~queries/useAccountsInfiniteQuery';
import useDepartmentsInfiniteQuery from '~queries/useDepartmentsInfiniteQuery';
import useDriversInfiniteQuery from '~queries/useDriversInfiniteQuery';
import useEquipmentOwnersInfiniteQuery from '~queries/useEquipmentOwnersInfiniteQuery';
import useOrderStatusInfiniteQuery from '~queries/useOrderStatusInfiniteQuery';
import usePrevailingWageInfiniteQuery from '~queries/usePrevailingWageInfiniteQuery';
import useProjectsInfiniteQuery from '~queries/useProjectsInfiniteQuery';
import useRateTypesInfiniteQuery from '~queries/useRateTypesInfiniteQuery';
import useRequestersInfiniteQuery from '~queries/useRequestersInfiniteQuery';
import useWaypointInfiniteQuery from '~queries/useWaypointInfiniteQuery';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import DriverPayQueryContext from '../../DriverPayQueryContext';

const QUERY_PREFIX = 'DriverPayPage_OrderDriverPay';

enum DriverPayAvailableFilter {
  CUSTOMERS = 'customers',
  DEPARTMENTS = 'departments',
  DRIVERS = 'drivers',
  DROPOFF_SITES = 'dropoff_sites',
  EQUIPMENT_OWNERS = 'equipment_owners',
  ORDER_STATES = 'order_states',
  PICKUP_SITES = 'pickup_sites',
  PREVAILING_WAGE = 'prevailing_wage',
  PROJECTS = 'projects',
  REQUESTERS = 'requesters',
  RATE_TYPES = 'rate_types',
  VENDORS = 'vendors',
}

type SearchQueryState = {
  input: string;
  debounced: string;
};

export default function OrdersFilters() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const { filters: appliedFilters, applyFilters } = useContext(DriverPayQueryContext);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<DriverPayAvailableFilter>();
  const [searchQuery, setSearchQuery] = useState<SearchQueryState>({
    input: '',
    debounced: '',
  });

  const customersQuery = useAccountsInfiniteQuery(
    `${QUERY_PREFIX}_customers`,
    companyId,
    AccountType.CUSTOMER,
  );
  const vendorsQuery = useAccountsInfiniteQuery(
    `${QUERY_PREFIX}_vendors`,
    companyId,
    AccountType.VENDOR,
  );
  const driversQuery = useDriversInfiniteQuery(`${QUERY_PREFIX}_drivers`, companyId);
  const equipmentOwnersQuery = useEquipmentOwnersInfiniteQuery(
    `${QUERY_PREFIX}_equipmentOwners`,
    companyId,
    { id: companyId, name: userStore.currentCompanies?.[0]?.legalName ?? '' },
  );
  const projectsQuery = useProjectsInfiniteQuery(`${QUERY_PREFIX}_projects`, companyId);
  const departmentsQuery = useDepartmentsInfiniteQuery(
    `${QUERY_PREFIX}_departments`,
    companyId,
  );
  const orderStatusQuery = useOrderStatusInfiniteQuery();
  const requestersQuery = useRequestersInfiniteQuery(
    `${QUERY_PREFIX}_requesters`,
    companyId,
  );
  const prevailingWageQuery = usePrevailingWageInfiniteQuery();
  const rateTypesQuery = useRateTypesInfiniteQuery();

  // FIXME @todo Add filters
  const pickupSitesQuery = useWaypointInfiniteQuery(
    `${QUERY_PREFIX}_pickupSites`,
    companyId,
    'pickup',
    {
      customerAccountIds: appliedFilters.customerAccountIds,
      departmentIds: appliedFilters.departmentIds,
      dispatchNumberIds: appliedFilters.dispatchNumberIds,
      driverIds: appliedFilters.driverIds,
      dropoffSiteIds: appliedFilters.dropoffSiteIds,
      endDate: appliedFilters.endDate?.endOf('day').toISOString(),
      jobStates: appliedFilters.jobStates,
      orderStates: appliedFilters.orderStates,
      projectIds: appliedFilters.projectIds,
      projectExternalIds: appliedFilters.projectExternalIds,
      requesterIds: appliedFilters.requesterIds,
      siteTypes: appliedFilters.siteTypes,
      startDate: appliedFilters.startDate?.startOf('day').toISOString(),
      vendorAccountIds: appliedFilters.vendorAccountIds,
    },
  );
  // FIXME @todo Add filters
  const dropffSitesQuery = useWaypointInfiniteQuery(
    `${QUERY_PREFIX}_dropffSites`,
    companyId,
    'drop_off',
    {
      customerAccountIds: appliedFilters.customerAccountIds,
      departmentIds: appliedFilters.departmentIds,
      dispatchNumberIds: appliedFilters.dispatchNumberIds,
      driverIds: appliedFilters.driverIds,
      endDate: appliedFilters.endDate?.endOf('day').toISOString(),
      orderStates: appliedFilters.orderStates,
      pickupSiteIds: appliedFilters.pickupSiteIds,
      projectIds: appliedFilters.projectIds,
      requesterIds: appliedFilters.requesterIds,
      startDate: appliedFilters.startDate?.startOf('day').toISOString(),
      vendorAccountIds: appliedFilters.vendorAccountIds,
    },
  );

  const driverPayQueryFilterKeyByDriverPayAvailableFilter: {
    [key in DriverPayAvailableFilter]: keyof typeof appliedFilters;
  } = {
    [DriverPayAvailableFilter.CUSTOMERS]: 'customerAccountIds',
    [DriverPayAvailableFilter.DEPARTMENTS]: 'departmentIds',
    [DriverPayAvailableFilter.DRIVERS]: 'driverIds',
    [DriverPayAvailableFilter.DROPOFF_SITES]: 'dropoffSiteIds',
    [DriverPayAvailableFilter.EQUIPMENT_OWNERS]: 'equipmentOwnerCompanyIds',
    [DriverPayAvailableFilter.ORDER_STATES]: 'orderStates',
    [DriverPayAvailableFilter.PICKUP_SITES]: 'pickupSiteIds',
    [DriverPayAvailableFilter.PREVAILING_WAGE]: 'prevailingWage',
    [DriverPayAvailableFilter.PROJECTS]: 'projectIds',
    [DriverPayAvailableFilter.RATE_TYPES]: 'rateTypes',
    [DriverPayAvailableFilter.REQUESTERS]: 'requesterIds',
    [DriverPayAvailableFilter.VENDORS]: 'vendorAccountIds',
  };

  const queriesByDriverPayAvailableFilter: Record<
    DriverPayAvailableFilter,
    InfiniteQuery
  > = {
    [DriverPayAvailableFilter.CUSTOMERS]: customersQuery,
    [DriverPayAvailableFilter.DEPARTMENTS]: departmentsQuery,
    [DriverPayAvailableFilter.DRIVERS]: driversQuery,
    [DriverPayAvailableFilter.DROPOFF_SITES]: dropffSitesQuery,
    [DriverPayAvailableFilter.EQUIPMENT_OWNERS]: equipmentOwnersQuery,
    [DriverPayAvailableFilter.ORDER_STATES]: orderStatusQuery,
    [DriverPayAvailableFilter.PICKUP_SITES]: pickupSitesQuery,
    [DriverPayAvailableFilter.PREVAILING_WAGE]: prevailingWageQuery,
    [DriverPayAvailableFilter.PROJECTS]: projectsQuery,
    [DriverPayAvailableFilter.RATE_TYPES]: rateTypesQuery,
    [DriverPayAvailableFilter.REQUESTERS]: requestersQuery,
    [DriverPayAvailableFilter.VENDORS]: vendorsQuery,
  };

  const activeQuery = useMemo(() => {
    if (!selectedFilter) {
      return null;
    }

    return queriesByDriverPayAvailableFilter[selectedFilter];
  }, [selectedFilter, queriesByDriverPayAvailableFilter]);

  const searchValueChangeCallback = useMemo(() => {
    if (activeQuery?.isDummyQuery) {
      return undefined;
    }

    return (value: string) => setSearchQuery((state) => ({ ...state, input: value }));
  }, [activeQuery?.isDummyQuery]);

  const selectedOptions = useMemo(() => {
    if (!selectedFilter) {
      return [];
    }

    const key = driverPayQueryFilterKeyByDriverPayAvailableFilter[selectedFilter];
    const options = appliedFilters[key] ?? [];

    return options as string[];
  }, [selectedFilter, appliedFilters]);

  const handleSelectedOptionsChange = useCallback(
    (options: string[]) => {
      if (selectedFilter) {
        const newSelectedOptions = options.length > 0 ? options : undefined;
        const key = driverPayQueryFilterKeyByDriverPayAvailableFilter[selectedFilter];

        applyFilters({ [key]: newSelectedOptions });
      }
    },
    [selectedOptions, selectedFilter],
  );

  const debouncedSetSearchQuery = useRef(
    debounce((value: string) => {
      setSearchQuery((state) => ({ ...state, debounced: value }));
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS),
  ).current;

  useEffect(() => {
    activeQuery?.fetch(searchQuery.debounced);
  }, [activeQuery?.fetch, searchQuery.debounced]);

  useEffect(() => {
    debouncedSetSearchQuery(searchQuery.input);

    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery, searchQuery.input]);

  useEffect(() => {
    if (!isMenuOpen) {
      setSelectedFilter(undefined);
      setSearchQuery((state) => ({ ...state, input: '' }));
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (!selectedFilter) {
      Object.values(queriesByDriverPayAvailableFilter).forEach((query) => {
        query.clear();
      });
    }
  }, [selectedFilter]);

  const filtersForChips = omit(appliedFilters, [
    'startDate',
    'endDate',
    'searchQuery',
    'invoiceCategory',
    'invoiceStates',
    'grouped',
    'companyId',
  ]);
  const hasFiltersForChips = Object.values(filtersForChips).some((filter) => {
    return Array.isArray(filter) ? filter.length > 0 : Boolean(filter);
  });
  const [filterChipsCollectedData, setFilterChipsCollectedData] = useState<
    Partial<Record<keyof typeof appliedFilters, FilterMenuOption[]>>
  >({});

  const handleRemoveAllFilters = useCallback((filters: string | string[]) => {
    const filtersArray = Array.isArray(filters) ? filters : [filters];
    const filtersObject = Object.fromEntries(
      filtersArray.map((filter) => [filter, undefined]),
    );

    applyFilters(filtersObject);
  }, []);

  useEffect(() => {
    if (selectedFilter && activeQuery?.data) {
      setFilterChipsCollectedData((state) => {
        const key = driverPayQueryFilterKeyByDriverPayAvailableFilter[selectedFilter];
        const updatedEntry = uniqBy(
          [...(state[key] ?? []), ...activeQuery.data],
          (option) => option.value,
        );

        const newCollectedData = {
          ...state,
          [key]: updatedEntry.length > 0 ? updatedEntry : undefined,
        };

        return newCollectedData;
      });
    }
  }, [selectedFilter, activeQuery?.data]);

  useEffect(() => {
    setFilterChipsCollectedData((state) => ({
      ...state,
      prevailingWage: prevailingWageQuery.data,
      rateTypes: rateTypesQuery.data,
    }));
  }, [prevailingWageQuery.data, rateTypesQuery.data]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Menu
        sx={{ [`& .${paperClasses.root}`]: { width: '250px' } }}
        onOpenStateChanged={setIsMenuOpen}
        menuTrigger={
          <SmallButton
            size="small"
            startIcon={<FilterList />}
            sx={(theme) => ({
              [`&.${buttonClasses.root}`]: {
                bgcolor: 'background.paper',
                color: theme.palette.text.primary,
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                borderRadius: theme.brandV2.borderRadius,
                ...(hasFiltersForChips
                  ? {
                      borderBottomLeftRadius: theme.brandV2.borderRadius,
                      borderBottomRightRadius: 0,
                      borderTopLeftRadius: theme.brandV2.borderRadius,
                      borderTopRightRadius: 0,
                    }
                  : { borderRadius: theme.brandV2.borderRadius }),
              },
            })}
          >
            {t('common.filters')}
          </SmallButton>
        }
      >
        {selectedFilter && (
          <FilterMenuItem
            focusSearchFieldOnMount
            loadingReason={activeQuery?.fetchReason}
            onFetchMore={activeQuery?.fetchMore}
            onSearchValueChange={searchValueChangeCallback}
            onSelectedOptionsChange={handleSelectedOptionsChange}
            options={activeQuery?.data ?? []}
            searchValue={searchQuery.input}
            selectAllOptionLabel={`${t('dispatch.dispatch_v2.filters.all_entities', { entity: t(`dispatch.dispatch_v2.filters.${snakeCase(selectedFilter)}`) })}`}
            selectedOptions={selectedOptions}
          />
        )}

        {!selectedFilter &&
          Object.values(DriverPayAvailableFilter).map((filter) => (
            <MenuItem key={filter} onClick={() => setSelectedFilter(filter)}>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={(theme) => ({ color: theme.brandV2.colors.treadBlack })}
                >
                  {t(`dispatch.dispatch_v2.filters.${snakeCase(filter)}`)}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {Boolean(
                    appliedFilters[
                      driverPayQueryFilterKeyByDriverPayAvailableFilter[filter]
                    ],
                  ) && (
                    <Check
                      sx={(theme) => ({
                        color: theme.brandV2.colors.treadOrange,
                        fontSize: '16px',
                      })}
                    />
                  )}

                  <ChevronRight sx={{ fontSize: '16px' }} />
                </Box>
              </Box>
            </MenuItem>
          ))}
      </Menu>

      <AppliedFiltersChips
        appliedFilters={filtersForChips}
        collectedData={filterChipsCollectedData}
        onRemoveAllFilters={handleRemoveAllFilters}
        onRemoveFilter={handleRemoveAllFilters}
      />
    </Box>
  );
}
